import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthSync } from '@/components/AuthSync';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RoleGuard } from '@/components/RoleGuard';
import { OrganizationRoleGuard } from '@/components/OrganizationRoleGuard';
import { FeatureGate } from '@/components/FeatureGate';
import { SatGuard } from '@/components/SatGuard';
import { Toaster } from 'sonner';
import { Suspense, lazy, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { StudentsLandingPage } from './modules/marketing/landing/StudentsLandingPage';
import { SatLandingPage } from './modules/marketing/landing/SatLandingPage';
import { ParentsLandingPage } from './modules/marketing/landing/ParentsLandingPage';
import { TutorsLandingPage } from './modules/marketing/landing/TutorsLandingPage';
import { Feature } from './contexts/FeaturesContext';
import { MarketingLayout } from './modules/marketing/components/MarketingLayout';
import { HomePage as MarketingHomePage } from './modules/marketing/pages/HomePage';
import { HowItWorksPage } from './modules/marketing/pages/HowItWorksPage';
import { AboutPage } from './modules/marketing/pages/AboutPage';
import { FaqPage } from './modules/marketing/pages/FaqPage';
import { BlogIndexPage } from './modules/marketing/pages/BlogIndexPage';
import { BlogPostPage } from './modules/marketing/pages/BlogPostPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SatAiFloatingButton } from '@/components/SatAiFloatingButton';
import { OpenRouteGate } from './pages/open/OpenRouteGate';

// Dev-only review page for the manta character (specs/012). `import.meta.env.DEV` is a
// build-time constant, so the route and its chunk are compiled out of production builds.
const MantaPreviewPage = import.meta.env.DEV
  ? lazy(() => import('@/components/marketing/manta/MantaPreviewPage'))
  : null;
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const TermsPage = lazy(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const OrganizationJoinPage = lazy(() => import('@/pages/OrganizationJoinPage').then((m) => ({ default: m.OrganizationJoinPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const RequestSessionPage = lazy(() => import('@/pages/RequestSessionPage').then((m) => ({ default: m.RequestSessionPage })));
const RequestRecurringSeriesPage = lazy(() => import('@/pages/RequestRecurringSeriesPage').then((m) => ({ default: m.RequestRecurringSeriesPage })));
const ChatsPage = lazy(() => import('@/pages/ChatsPage').then((m) => ({ default: m.ChatsPage })));
const GroupInfoPage = lazy(() => import('@/pages/GroupInfoPage'));
const SessionsPage = lazy(() => import('@/pages/SessionsPage').then((m) => ({ default: m.SessionsPage })));
const CalendarPage = lazy(() => import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const ParentDashboard = lazy(() => import('@/pages/ParentDashboard').then((m) => ({ default: m.ParentDashboard })));
const StudentLinkPage = lazy(() => import('@/pages/StudentLinkPage').then((m) => ({ default: m.StudentLinkPage })));
const OrganizationLayout = lazy(() => import('@/pages/organization/OrganizationLayout').then((m) => ({ default: m.OrganizationLayout })));
const OrganizationOverviewPage = lazy(() => import('@/pages/organization/OverviewPage').then((m) => ({ default: m.OrganizationOverviewPage })));
const MembersPage = lazy(() => import('@/pages/organization/MembersPage').then((m) => ({ default: m.MembersPage })));
const InvitesPage = lazy(() => import('@/pages/organization/InvitesPage').then((m) => ({ default: m.InvitesPage })));
const OrganizationSettingsPage = lazy(() => import('@/pages/organization/SettingsPage').then((m) => ({ default: m.OrganizationSettingsPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const QuestionEditorPage = lazy(() => import('@/pages/QuestionEditorPage').then((m) => ({ default: m.QuestionEditorPage })));
const SatStudentProfilePage = lazy(() => import('@/pages/SatStudentProfilePage'));
const SatQuestionEditorPage = lazy(() => import('./pages/SatQuestionEditorPage').then((m) => ({ default: m.SatQuestionEditorPage })));
const SatPage = lazy(() => import('./pages/SatPage'));
const SatPracticeResult = lazy(() => import('./pages/SatPracticeResult'));
const SatDiagnosticResult = lazy(() => import('./pages/SatDiagnosticResult'));
const SatPlayerRouter = lazy(() => import('./pages/SatPlayerRouter'));
// Open Dashboard (specs/009): /what-we-do/sat IS the app now — the real course
// home, rendered for anyone, off browser-held progress. Lazy like the rest of SAT.
const SatOpenCoursePage = lazy(() => import('./pages/open/SatOpenCoursePage'));
const SatOpenDiagnosticPage = lazy(() => import('./pages/open/SatOpenDiagnosticPage'));
const SatOpenDiagnosticResultPage = lazy(() => import('./pages/open/SatOpenDiagnosticResultPage'));
const SatOpenPracticePage = lazy(() => import('./pages/open/SatOpenPracticePage'));
const SatOpenPracticeResultPage = lazy(() => import('./pages/open/SatOpenPracticeResultPage'));

function PageLoader() {
  return (
    <div
      className="fixed inset-0 z-40 flex min-h-screen items-center justify-center bg-background/95 px-6 backdrop-blur-sm"
      role="status"
      aria-label="Loading page"
    >
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
    </div>
  );
}

function withPageSuspense(node: ReactNode) {
  return <Suspense fallback={<PageLoader />}>{node}</Suspense>;
}

/** Every open (`/what-we-do/sat/*`) route goes through the gate: signed-in SAT
 *  students are sent to /sat, and auth-restore never flashes the open page. */
function withOpenGate(node: ReactNode) {
  return withPageSuspense(<OpenRouteGate fallback={<PageLoader />}>{node}</OpenRouteGate>);
}

function withSatSuspense(node: ReactNode) {
  return <Suspense fallback={<PageLoader />}>{node}</Suspense>;
}

export function Router() {
    return <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthSync />
              <Toaster position="top-right" richColors closeButton />
              <SatAiFloatingButton />
            <Routes>
            {MantaPreviewPage && <Route path="/dev/manta" element={withPageSuspense(<MantaPreviewPage />)} />}
            {/* Marketing pages — wrapped in shared layout */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<MarketingHomePage />} />
              <Route path="/what-we-do/students" element={<StudentsLandingPage />} />
              <Route path="/what-we-do/students/" element={<StudentsLandingPage />} />
              {/* Ghost Door (specs/008): the SAT "what we offer" long-form marketing
                  content moved here — /what-we-do/sat itself is now the door (below,
                  outside MarketingLayout — it's the app, not a marketing page). */}
              <Route path="/what-we-do/sat/about" element={<SatLandingPage />} />
              <Route path="/what-we-do/sat/about/" element={<SatLandingPage />} />
              <Route path="/what-we-do/parents" element={<ParentsLandingPage />} />
              <Route path="/what-we-do/parents/" element={<ParentsLandingPage />} />
              <Route path="/what-we-do/tutors" element={<TutorsLandingPage />} />
              <Route path="/what-we-do/tutors/" element={<TutorsLandingPage />} />
              <Route path="/how-it-works/:role" element={<HowItWorksPage />} />
              <Route path="/how-it-works/:role/" element={<HowItWorksPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about/" element={<AboutPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/faq/" element={<FaqPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/blog/:slug/" element={<BlogPostPage />} />
            </Route>

            {/* Legacy redirect */}
            <Route path="/what-we-do/choose-role" element={<Navigate to="/" replace />} />

            {/* Open Dashboard (specs/009): the real SAT course home, open to
                anyone — the app, not marketing chrome, so it lives outside
                MarketingLayout with its own header (OpenMetaStrip). */}
            <Route path="/what-we-do/sat" element={withOpenGate(<SatOpenCoursePage />)} />
            <Route path="/what-we-do/sat/" element={withOpenGate(<SatOpenCoursePage />)} />

            {/* Anonymous play + results. The REAL Bluebook player and the REAL
                score report, driven by browser-held progress (openTransports).
                Public by construction: no ProtectedRoute, no SatGuard. */}
            <Route path="/what-we-do/sat/diagnostic" element={withOpenGate(<SatOpenDiagnosticPage />)} />
            <Route path="/what-we-do/sat/diagnostic/result" element={withOpenGate(<SatOpenDiagnosticResultPage />)} />
            <Route path="/what-we-do/sat/practice/:setId" element={withOpenGate(<SatOpenPracticePage />)} />
            <Route path="/what-we-do/sat/practice/:setId/result" element={withOpenGate(<SatOpenPracticeResultPage />)} />

            {/* Public routes */}
            <Route path="/login" element={withPageSuspense(<LoginPage />)} />
            <Route path="/register" element={withPageSuspense(<RegisterPage />)} />
            <Route path="/forgot-password" element={withPageSuspense(<ForgotPasswordPage />)} />
            <Route path="/reset-password" element={withPageSuspense(<ResetPasswordPage />)} />
            <Route path="/terms" element={withPageSuspense(<TermsPage />)} />
            <Route path="/privacy" element={withPageSuspense(<PrivacyPage />)} />
            <Route path="/organization/join" element={withPageSuspense(<FeatureGate feature={Feature.ORGANIZATIONS}><OrganizationJoinPage /></FeatureGate>)} />

            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<DashboardPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/request-session" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<RequestSessionPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/request-recurring" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<RequestRecurringSeriesPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chats" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<ChatsPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chats/:channelId" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<ChatsPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chats/:channelId/manage" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<GroupInfoPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sessions" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<SessionsPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/calendar" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<CalendarPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<SettingsPage />)}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payments" 
              element={
                <ProtectedRoute>
                  {withPageSuspense(<PaymentsPage />)}
                </ProtectedRoute>
              } 
            />

            {/* Parent routes */}
            <Route 
              path="/parent" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['PARENT']}>
                    {withPageSuspense(<ParentDashboard />)}
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />

            {/* Student parent link route */}
            <Route 
              path="/settings/parent-link" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['STUDENT']}>
                    {withPageSuspense(<StudentLinkPage />)}
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />

            {/* Organization Management - ORG_ADMIN only, behind feature flag */}
            <Route 
              path="/organization/manage" 
              element={
                <ProtectedRoute>
                  <FeatureGate feature={Feature.ORGANIZATIONS}>
                    <OrganizationRoleGuard allowedRoles={['ADMIN']}>
                      {withPageSuspense(<OrganizationLayout />)}
                    </OrganizationRoleGuard>
                  </FeatureGate>
                </ProtectedRoute>
              }
            >
              <Route path="overview" element={withPageSuspense(<OrganizationOverviewPage />)} />
              <Route path="members" element={withPageSuspense(<MembersPage />)} />
              <Route path="invites" element={withPageSuspense(<InvitesPage />)} />
              <Route path="settings" element={withPageSuspense(<OrganizationSettingsPage />)} />
              <Route index element={<Navigate to="overview" replace />} />
            </Route>

            {/* Admin-only routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['ADMIN']}>
                    {withPageSuspense(<AdminPage />)}
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/:tab" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['ADMIN']}>
                    {withPageSuspense(<AdminPage />)}
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/questions/editor/:id" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['ADMIN']}>
                    {withPageSuspense(<QuestionEditorPage />)}
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />

            {/* SAT Module routes — lazy-loaded with Suspense (P3-CS01) */}
            <Route 
              path="/sat/:tab?" 
              element={
                <ProtectedRoute>
                  <SatGuard>
                    {withSatSuspense(<SatPage />)}
                  </SatGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sat/questions/editor/:id" 
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['ADMIN']}>
                      {withSatSuspense(<SatQuestionEditorPage />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sat/diagnostic/:id" 
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['STUDENT']}>
                      {withSatSuspense(<SatPlayerRouter mode="diagnostic" />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sat/diagnostic/:id/result" 
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['STUDENT', 'TUTOR', 'ADMIN']}>
                      {withSatSuspense(<SatDiagnosticResult />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sat/practice/:id" 
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['STUDENT']}>
                      {withSatSuspense(<SatPlayerRouter mode="practice" />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/sat/practice/:id/result"
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['STUDENT', 'TUTOR', 'ADMIN']}>
                      {withSatSuspense(<SatPracticeResult />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sat/students/:studentId/profile"
              element={
                <ProtectedRoute>
                  <SatGuard>
                    <RoleGuard allowedRoles={['ADMIN', 'TUTOR']}>
                      {withSatSuspense(<SatStudentProfilePage />)}
                    </RoleGuard>
                  </SatGuard>
                </ProtectedRoute>
              }
            />

            {/* Catch-all 404 */}
            {/* Inside the site shell so a lost visitor still has the header and footer. */}
            <Route element={<MarketingLayout />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            </Routes>
            </BrowserRouter>;
}
